import ArrowRight16 from '@carbon/icons-react/es/arrow--right/16';
import { Col, Divider, ModalProps, Row } from 'antd';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { AccountStatus } from 'api/account';
import {
  BillingCycle,
  ChangePlanEstimateResponse,
  LicenseCapLimit,
  MonthlyPrice,
  PlanHierarchy,
  RestrictedPlans,
  Subscription,
  SubscriptionForm,
  SubscriptionStatus,
  SupportedPlans as Plans,
  UpgradeTo,
} from 'api/billing/types';
import { Button } from 'components/atoms/button';
import { InputNumber } from 'components/atoms/input';
import { Loading } from 'components/atoms/loading';
import { Modal } from 'components/atoms/modal';
import { Radio } from 'components/atoms/radio';
import { useAccount, useIsAccountActive } from 'hooks/use-account';
import { useTranslate } from 'hooks/use-translation';
import {
  useAddLicensesEstimateMutation,
  useBillingVersion,
  useChangePlanEstimateMutation,
  usePlanName,
} from 'queries/billing';
import {
  formatAmount,
  getCurrentBillingCycle,
  getPlanName,
  getPlanNameWithCycle,
  getPlanPrice,
} from '../utils';

const StyledModal = styled(Modal)`
  height: 518px;
  padding-bottom: 0;

  &:after {
    background: linear-gradient(-45deg, rgba(0, 0, 0, 0.45) 8px, transparent 0),
      linear-gradient(45deg, rgba(0, 0, 0, 0.45) 8px, transparent 0);
    background-position: left-bottom;
    background-repeat: repeat-x;
    background-size: 16px 16px;
    content: ' ';
    display: block;
    position: absolute;
    bottom: 0px;
    left: 0px;
    width: 100%;
    height: 14px;
  }

  .ant-modal-content {
    height: 100%;
    border-top: 3px solid var(--green-50);
    box-shadow: var(--shadow-24);
  }

  .ant-modal-body {
    margin-top: var(--spacing-05);
  }

  p {
    margin: 0;
  }
`;

const Label = styled.div<{
  isTrial?: boolean;
}>`
  font-size: var(--font-size-sm);
  color: var(--gray-80);
  margin-bottom: var(--spacing-02);
  visibility: ${(props) => (props.isTrial ? 'hidden' : 'visible')};
`;

const PurchaseButton = styled(Button)`
  background-color: var(--green-50) !important;
  border-color: var(--green-50) !important;

  p {
    flex: 1;
    text-align: left;
  }
`;

const EstimateWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--spacing-03);
`;

const StrikeThrough = styled.p`
  text-decoration: line-through;
`;

const Splitup = styled.p`
  color: var(--gray-50);
  margin-top: var(--spacing-02) !important;
  font-size: var(--font-size-sm);
`;

const UpgradeForm = ({
  upgradeTo,
  subscription,
  onConfirm,
  isAccountActive,
}: {
  upgradeTo: UpgradeTo;
  subscription: Subscription;
  onConfirm: (formValues: SubscriptionForm) => void;
  isAccountActive: boolean;
}) => {
  const { status } = useAccount();
  const [licenseCount, setLicenseCount] = useState(
    upgradeTo.licenseCount ?? subscription.licenseQuantity,
  );
  const { mutateAsync: changePlanEstimate, isLoading } =
    useChangePlanEstimateMutation();

  const {
    mutateAsync: addLicensesEstimate,
    isLoading: isFetchingLicenseEstimate,
  } = useAddLicensesEstimateMutation();

  const [monthlyEstimate, setMonthlyEstimate] =
    useState<ChangePlanEstimateResponse | null>(null);
  const [annualEstimate, setAnnualEstimate] =
    useState<ChangePlanEstimateResponse | null>(null);

  const [activeEstimate, setActiveEstimate] =
    useState<ChangePlanEstimateResponse | null>(null);

  const currentBillingCycle = useMemo(
    () => getCurrentBillingCycle(subscription.plan.planName),
    [subscription.plan.planName],
  );
  const [billingCycle, setBillingCycle] = useState(
    upgradeTo.billingCycle ??
      (upgradeTo.licenseCount ? currentBillingCycle : BillingCycle.Yearly),
  );
  const targetPlan = upgradeTo.plan ?? getPlanName(subscription.plan.planName);

  const targetPlanName = useMemo(
    () => getPlanNameWithCycle(targetPlan, billingCycle),
    [billingCycle, targetPlan],
  );

  const isAddingLicenses: boolean = useMemo(
    () =>
      Object.keys(upgradeTo).includes('licenseCount') &&
      Object.keys(upgradeTo).length === 1 &&
      subscription.subscriptionStatus === SubscriptionStatus.ACTIVE,
    [subscription.subscriptionStatus, upgradeTo],
  );

  const minLicenseCount = useMemo(
    () => upgradeTo.licenseCount ?? subscription.licenseQuantity,
    [subscription.licenseQuantity, upgradeTo.licenseCount],
  );

  const maxLicenseCount = useMemo(
    () =>
      targetPlan !== Plans.Professional
        ? targetPlan !== Plans.Essentials
          ? undefined
          : isAddingLicenses
          ? LicenseCapLimit.Essentials - subscription.licenseQuantity
          : LicenseCapLimit.Essentials
        : isAddingLicenses
        ? LicenseCapLimit.Professional - subscription.licenseQuantity
        : LicenseCapLimit.Professional,
    [isAddingLicenses, subscription.licenseQuantity, targetPlan],
  );

  useEffect(() => {
    setActiveEstimate(
      billingCycle === BillingCycle.Yearly ? annualEstimate : monthlyEstimate,
    );
  }, [annualEstimate, billingCycle, monthlyEstimate]);

  const canSwitchBillingCycle = useMemo(
    () =>
      ![
        Plans.Enterprise,
        Plans['Enterprise-V2'],
        RestrictedPlans['Enterprise-Tiered'],
        RestrictedPlans['Enterprise-Tiered-V2'],
      ].includes(targetPlan) &&
      (subscription.subscriptionStatus !== SubscriptionStatus.ACTIVE ||
        (upgradeTo.plan && currentBillingCycle === BillingCycle.Monthly)),
    [
      currentBillingCycle,
      targetPlan,
      upgradeTo.plan,
      subscription.subscriptionStatus,
    ],
  );

  const getChangePlanMonthlyEstimate = useCallback(async () => {
    const response = await changePlanEstimate({
      plan: targetPlan,
      form: {
        billing_cycle: BillingCycle.Monthly,
        quantity: licenseCount,
      },
      subscription,
    });

    setMonthlyEstimate(response);
  }, [changePlanEstimate, licenseCount, subscription, targetPlan]);

  const getChangePlanAnnualEstimate = useCallback(async () => {
    const response = await changePlanEstimate({
      plan: targetPlan,
      form: {
        billing_cycle: BillingCycle.Yearly,
        quantity: licenseCount,
      },
      subscription,
    });

    setAnnualEstimate(response);
  }, [changePlanEstimate, licenseCount, subscription, targetPlan]);

  const getAddLicenseEstimate = useCallback(async () => {
    const response = await addLicensesEstimate(licenseCount);

    setActiveEstimate(response);
  }, [addLicensesEstimate, licenseCount]);

  const getEstimates = useCallback(() => {
    if (isAddingLicenses) {
      getAddLicenseEstimate();

      return;
    }
    if (canSwitchBillingCycle) {
      getChangePlanMonthlyEstimate();
    }
    getChangePlanAnnualEstimate();
  }, [
    canSwitchBillingCycle,
    getAddLicenseEstimate,
    getChangePlanAnnualEstimate,
    getChangePlanMonthlyEstimate,
    isAddingLicenses,
  ]);

  const clearEstimates = useCallback(() => {
    setMonthlyEstimate(null);
    setAnnualEstimate(null);
    setActiveEstimate(null);
  }, []);

  useEffect(() => {
    clearEstimates();
    getEstimates();
  }, [clearEstimates, getEstimates, licenseCount]);

  const onLicenseCountChange = useCallback((value) => {
    setLicenseCount(value);
  }, []);

  const onCycleChange = useCallback((event) => {
    setBillingCycle(event.target.value);
  }, []);

  const onSubmit = useCallback(() => {
    onConfirm({
      billing_cycle: billingCycle,
      quantity: licenseCount,
    });
  }, [billingCycle, licenseCount, onConfirm]);

  const planPrice = useMemo(
    () => getPlanPrice(targetPlanName),
    [targetPlanName],
  );

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <Label>
          <FormattedMessage id="billing.plan" />
        </Label>
        <b>
          <FormattedMessage id={`billing.plan.${targetPlan}`.toLowerCase()} />
        </b>
      </Col>
      <Col span={24}>
        <Label>
          <FormattedMessage id="billing.cycle" />
        </Label>
        <b>
          {canSwitchBillingCycle ? (
            <Radio.Group value={billingCycle} onChange={onCycleChange}>
              <Radio value={BillingCycle.Yearly}>
                <FormattedMessage id="billing.cycle.option.yearly" />
              </Radio>

              <Radio value={BillingCycle.Monthly}>
                <FormattedMessage id="billing.cycle.option.monthly" />
              </Radio>
            </Radio.Group>
          ) : (
            <FormattedMessage id={`billing.cycle.option.${billingCycle}`} />
          )}
        </b>
      </Col>
      <Col span={8}>
        <Label>
          <FormattedMessage id="billing.licenses" />
        </Label>
        <InputNumber
          size="small"
          value={licenseCount}
          min={minLicenseCount}
          max={maxLicenseCount}
          style={{ width: 'min-content' }}
          onChange={onLicenseCountChange}
          autoFocus={isAddingLicenses}
        />
        {planPrice && (
          <Splitup>
            <FormattedMessage
              id="billing.estimate.splitup.line.break"
              values={{
                br: <br />,
                inviteCount: licenseCount,
                currencyCode: subscription?.currency,
                price: planPrice,
                startDate: moment().format('MMM DD'),
                endDate: (upgradeTo.billingCycle ||
                status === AccountStatus.Trial ||
                !isAccountActive
                  ? moment().add(
                      1,
                      billingCycle === BillingCycle.Monthly ? 'month' : 'year',
                    )
                  : moment(subscription?.endDate)
                ).format("MMM DD'YY"),
              }}
            />
          </Splitup>
        )}
      </Col>
      <Col span={16} style={{ textAlign: 'right' }}>
        <Label isTrial={status === AccountStatus.Trial}>
          <FormattedMessage id="billing.pro.rated" />
        </Label>
        {isLoading || isFetchingLicenseEstimate || !activeEstimate ? (
          <Loading size="small" />
        ) : (
          <EstimateWrapper>
            {billingCycle === BillingCycle.Yearly && monthlyEstimate && (
              <StrikeThrough>
                {MonthlyPrice[targetPlan] * licenseCount * 12}{' '}
                {monthlyEstimate.currencyCode}
              </StrikeThrough>
            )}
            <p>
              {formatAmount(activeEstimate.invoiceTotal)}{' '}
              {activeEstimate.currencyCode}
            </p>
          </EstimateWrapper>
        )}
      </Col>
      <Col span={24}>
        <Divider style={{ margin: 0 }} />
      </Col>
      {activeEstimate && activeEstimate.creditsApplied > 0 && (
        <>
          <Col span={16}>
            <FormattedMessage id="billing.estimate.credits.applied" />
          </Col>
          <Col span={8} style={{ textAlign: 'right' }}>
            <p>
              {formatAmount(activeEstimate.creditsApplied)}{' '}
              {activeEstimate.currencyCode}
            </p>
          </Col>
        </>
      )}
      <Col span={12}>
        <b>
          <FormattedMessage id="billing_total_amount_due" />
        </b>
      </Col>
      <Col span={12} style={{ textAlign: 'right' }}>
        {isLoading || !activeEstimate ? (
          <Loading size="small" />
        ) : (
          <b>
            {formatAmount(activeEstimate.amountDue)}{' '}
            {activeEstimate.currencyCode}
          </b>
        )}
      </Col>
      <Col span={24}>
        <PurchaseButton
          type="primary"
          style={{ width: '100%' }}
          onClick={onSubmit}
          icon={<ArrowRight16 />}
          iconPlacement="right"
        >
          <p>
            <FormattedMessage id="billing.purchase.subscription" />
          </p>
        </PurchaseButton>
      </Col>
      <Col span={24}>
        <Label>
          <FormattedMessage id="billing.estimate.disclaimer" />
        </Label>
      </Col>
    </Row>
  );
};

export const EstimateModal = ({
  subscription,
  upgradeTo,
  onConfirm,
  onCancel,
}: ModalProps & {
  upgradeTo: UpgradeTo;
  subscription: Subscription;
  onConfirm: (formValues: SubscriptionForm) => void;
}) => {
  const { status: accountStatus } = useAccount();
  const version = useBillingVersion();
  const isAccountActive = useIsAccountActive();
  const translate = useTranslate();
  const currentPlanName = useMemo(
    () => getPlanName(subscription.plan.planName),
    [subscription.plan.planName],
  );

  const targetPlanNameTranslated = usePlanName(upgradeTo.plan);

  const currentPlanNameTranslated = usePlanName(currentPlanName);

  const getTrialTitle = useCallback(() => {
    const targetPlanName = upgradeTo.plan;

    if (!targetPlanName || !version) {
      return '';
    }
    const _planHierarchy = PlanHierarchy[version];
    const isDowngrade =
      _planHierarchy[currentPlanName] > _planHierarchy[targetPlanName];

    const isCurrentPlan =
      _planHierarchy[currentPlanName] === _planHierarchy[targetPlanName];

    return isDowngrade
      ? translate('billing.downgrade.to.plan.button', {
          planName: targetPlanNameTranslated,
        })
      : isCurrentPlan
      ? translate('billing.continue.with.plan.button', {
          planName: currentPlanNameTranslated,
        })
      : translate('billing.upgrade.to.plan.button', {
          planName: targetPlanNameTranslated,
        });
  }, [
    upgradeTo.plan,
    version,
    currentPlanName,
    translate,
    targetPlanNameTranslated,
    currentPlanNameTranslated,
  ]);
  const title = useMemo(
    () =>
      accountStatus === AccountStatus.Trial || !isAccountActive ? (
        getTrialTitle()
      ) : upgradeTo.plan ? (
        <FormattedMessage
          id="billing.plan.upgrade.title"
          values={{
            planName: targetPlanNameTranslated,
          }}
        />
      ) : upgradeTo.billingCycle ? (
        <FormattedMessage id="billing.cycle.upgrade.title" />
      ) : upgradeTo.licenseCount ? (
        <FormattedMessage id="billing.add.licenses" />
      ) : (
        <FormattedMessage id="upgrade" />
      ),
    [
      accountStatus,
      isAccountActive,
      getTrialTitle,
      upgradeTo.plan,
      upgradeTo.billingCycle,
      upgradeTo.licenseCount,
      targetPlanNameTranslated,
    ],
  );

  return (
    <StyledModal
      maskClosable={false}
      onCancel={onCancel}
      width={462}
      visible
      title={title}
      footer={null}
    >
      <UpgradeForm
        upgradeTo={upgradeTo}
        subscription={subscription}
        onConfirm={onConfirm}
        isAccountActive={isAccountActive}
      />
    </StyledModal>
  );
};
